import { request } from './request';

const postJSON = (body: object): RequestInit => ({
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

export type SeatInterval = "monthly" | "yearly" | "three_year";

export type SeatStatus = "active" | "past_due" | "unpaid" | "canceled" | null;

export interface ISeatPrice {
  interval: SeatInterval;
  amount: number; // smallest currency unit, e.g. 12900 = $129.00
  currency: string;
}

export interface ISeatQuote {
  seat_type: string;
  trial_days: number;
  prices: ISeatPrice[];
}

export interface ISeatCheckoutParams {
  seat_type: string;
  interval: SeatInterval;
  success_url: string;
  cancel_url: string;
  msp_id?: string; // omitted from the landing site for now; kept for a future MSP selector
}

export interface ISeatCheckoutResponse {
  seat_grant_id: string;
  checkout_url: string;
}

export interface ISeatStatus {
  status: SeatStatus;
  seat_grant_id: string;
  seat_type: string | null;
  interval: SeatInterval | null;
  quantity: number | null;
  instance: string | null;
  msp_id: string | null;
  subject_ids: string[];
  is_trial: boolean;
  trial_end: number | null;
  cancel_at_period_end: boolean | null;
  period_end: number | null;
  // Whether `instance` is served by the shared portal SPA (same routing as
  // /instance/list and /user/membership's use_portal). Optional; defaults to
  // false (classic per-instance domain) when absent.
  use_portal?: boolean;
}

// Public price list — do not use `request`, it redirects to login when unsigned-in.
// `out_of_stock` covers the whole Crystal hardware product, not a specific
// seat_type/plan - it's inventory-level, not per-price.
export const quoteSeatsAsync = (): Promise<{ seats: ISeatQuote[]; out_of_stock?: boolean }> =>
  fetch(`${process.env.REACT_APP_API_SERVER}/seat/quote`, postJSON({})).then((res) =>
    res.json()
  );

// Buy a seat. Returns a Stripe-hosted checkout page to redirect the customer to.
export const checkoutSeatAsync = (
  params: ISeatCheckoutParams
): Promise<ISeatCheckoutResponse> => request("/seat/checkout", postJSON(params));

// Poll the grant until it is live on an instance.
export const seatStatusAsync = (seat_grant_id: string): Promise<ISeatStatus> =>
  request("/seat/status", postJSON({ seat_grant_id }));

// Format an amount in the smallest currency unit into a display string, e.g.
// 12900 -> "$129" (whole dollars drop the decimal), 9950 -> "$99.50".
export function formatSeatAmount(amount: number, currency: string): string {
  const value = amount / 100;
  const fractionDigits = Number.isInteger(value) ? 0 : 2;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value);
  } catch {
    // Fallback if currency code is not recognized by Intl.
    return `$${value.toFixed(fractionDigits)}`;
  }
}

const INTERVAL_SUFFIX: Record<SeatInterval, string> = {
  monthly: "/month",
  yearly: "/year",
  three_year: "/3 years",
};

// Build the price line shown under the selected seat. With a trial the customer
// owes nothing on the day they buy, so say so explicitly.
export function formatSeatPriceLabel(
  price: ISeatPrice,
  trialDays: number
): string {
  const amountLabel = formatSeatAmount(price.amount, price.currency);
  const suffix = INTERVAL_SUFFIX[price.interval] ?? "";
  if (trialDays > 0) {
    return `Free for ${trialDays} days, then ${amountLabel}${suffix}`;
  }
  return `${amountLabel}${suffix}`;
}

// ============================================================
// license related
// ============================================================

export interface ICrystalOnboardingConfig {
  network: {
    mode: "auto" | "device" | "custom";
    device?: string;
    portMap?: { wan: string[]; lan: string[] };
  };
}

export interface ICrystalLicense {
  license_id: string;
  status: "available" | "bound" | "configured" | "iso_ready" | "activated";
  license_type: string | null;
  msp_id: string | null;
  onboarding_config: ICrystalOnboardingConfig | null;
  bound_ts: number;
}

export const fetchCrystalLicenses = (): Promise<{ licenses: ICrystalLicense[] }> =>
  request('/crystal/licenses', postJSON({}));
